import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import Button from 'components/Button';
import { conversationService } from 'api/services/ConversationService';
import { conversationMessageService } from 'api/services/ConversationMessageService';
import { userService } from 'api/services/UserService';
import type { ConversationViewModel } from 'api/types/conversation/ConversationViewModel';
import type { MessageViewModel } from 'api/types/conversation/MessageViewModel';
import 'bootstrap/dist/css/bootstrap.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';

const ConversationDetail: React.FC = () => {
  const { id: conversationId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [conversation, setConversation] = useState<ConversationViewModel | null>(null);
  const [otherParticipantName, setOtherParticipantName] = useState<string>('');
  const [messages, setMessages] = useState<MessageViewModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [closing, setClosing] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);

  const fetchConversation = useCallback(async (cid: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await conversationService.getById(cid);
      if (result.isSuccess && result.data) {
        setConversation(result.data);
        const otherId =
          result.data.senderId === user?.id ? result.data.receiverId : result.data.senderId;
        if (otherId) {
          const u = await userService.getById(otherId);
          const name = u.data
            ? [u.data.firstName, u.data.lastName].filter(Boolean).join(' ') || u.data.email || otherId
            : otherId;
          setOtherParticipantName(name);
        }
      } else {
        setError(result.message ?? 'Konuşma bulunamadı.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  const fetchMessages = useCallback(async (cid: string) => {
    const result = await conversationMessageService.getMessages(cid);
    if (result.isSuccess && result.data) {
      setMessages(Array.isArray(result.data) ? result.data : []);
    }
  }, []);

  useEffect(() => {
    if (conversationId && user?.id) {
      fetchConversation(conversationId);
    } else if (!user?.id) {
      setLoading(false);
      setError('Giriş yapmalısınız.');
    } else {
      setLoading(false);
      setError('Konuşma kodu belirtilmedi.');
    }
  }, [conversationId, user?.id, fetchConversation]);

  useEffect(() => {
    if (conversationId && conversation) {
      fetchMessages(conversationId);
    }
  }, [conversationId, conversation, fetchMessages]);

  const handleCloseConversation = async (): Promise<void> => {
    if (!conversationId || closing) return;
    setClosing(true);
    try {
      const result = await conversationService.closeConversation(conversationId);
      if (result.isSuccess) {
        setShowCloseConfirm(false);
        navigate('/');
      } else {
        alert(result.message ?? 'Sohbet kapatılamadı.');
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Bir hata oluştu.');
    } finally {
      setClosing(false);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    const content = newMessage.trim();
    if (!conversationId || !content || sending || conversation?.isClosed) return;
    setSending(true);
    try {
      const result = await conversationMessageService.sendMessage(conversationId, content);
      if (result.isSuccess) {
        setNewMessage('');
        await fetchMessages(conversationId);
      }
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary mb-2" role="status" />
        <p className="text-muted">Konuşma yükleniyor...</p>
      </div>
    );
  }

  if (error || !conversation) {
    return (
      <div className="container py-5 text-center">
        <p className="text-danger mb-3">{error ?? 'Konuşma bulunamadı.'}</p>
        <Link className="btn btn-primary" to="/mesajlar">
          Mesajlara Dön
        </Link>
      </div>
    );
  }

  return (
    <div className="container-fluid p-0 bg-light min-vh-100">
      <div className="container py-3">
        <div className="card shadow-sm">
          <div className="card-header bg-white border-bottom d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <Link
                className="btn btn-link text-muted text-decoration-none me-2"
                to="/mesajlar"
                aria-label="Geri"
              >
                <i className="fas fa-arrow-left" />
              </Link>
              <h5 className="mb-0">{otherParticipantName}</h5>
              {conversation.listingId && (
                <Link
                  className="btn btn-link btn-sm ms-2"
                  to={`/ilan-detay-login/${conversation.listingId}`}
                >
                  İlanı Görüntüle
                </Link>
              )}
            </div>
            <div className="dropdown">
              <Button
                className="btn btn-light btn-sm rounded-circle"
                type="button"
                id="conversationMenuDropdown"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                title="Menü"
              >
                <i className="fas fa-ellipsis-v" />
              </Button>
              <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="conversationMenuDropdown">
                <li>
                  <button
                    type="button"
                    className="dropdown-item py-2 text-danger"
                    onClick={() => setShowCloseConfirm(true)}
                    disabled={conversation.isClosed}
                  >
                    <i className="fas fa-times-circle me-2" />
                    Sohbeti Kapat
                  </button>
                </li>
              </ul>
            </div>
          </div>
          <div className="card-body p-3">
            {conversation.isClosed && (
              <div className="alert alert-secondary py-2 mb-3">
                <i className="fas fa-lock me-2" />
                Bu sohbet kapatıldı.
              </div>
            )}
            <div className="message-list">
              {messages.length === 0 ? (
                <p className="text-muted text-center py-4">Henüz mesaj yok.</p>
              ) : (
                messages.map((msg) => {
                  const isOwn = msg.senderId === user?.id;
                  return (
                    <div
                      key={msg.id}
                      className={`d-flex mb-3 ${isOwn ? 'justify-content-end' : 'justify-content-start'}`}
                    >
                      <div
                        className={`rounded px-3 py-2 ${
                          isOwn ? 'bg-primary text-white' : 'bg-light text-dark'
                        }`}
                        style={{ maxWidth: '75%' }}
                      >
                        <div className="small text-muted mb-1">
                          {msg.sentAt
                            ? new Date(msg.sentAt).toLocaleString('tr-TR')
                            : ''}
                        </div>
                        <div>{msg.content ?? ''}</div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            {!conversation.isClosed && (
              <form onSubmit={handleSendMessage} className="p-3 border-top bg-white">
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Mesajınızı yazın..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    disabled={sending}
                  />
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={sending || !newMessage.trim()}
                  >
                    {sending ? (
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                    ) : (
                      <i className="fas fa-paper-plane" />
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Close confirmation modal */}
      {showCloseConfirm && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1} role="dialog">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Sohbeti Kapat</h5>
                <Button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowCloseConfirm(false)}
                  aria-label="Kapat"
                />
              </div>
              <div className="modal-body">
                Bu sohbeti kapatmak istediğinize emin misiniz? Bu işlem geri alınamaz.
              </div>
              <div className="modal-footer">
                <Button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCloseConfirm(false)}
                >
                  İptal
                </Button>
                <Button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleCloseConversation}
                  disabled={closing}
                >
                  {closing ? (
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  ) : null}
                  Evet, Kapat
                </Button>
              </div>
            </div>
          </div>
          <div
            className="modal-backdrop fade show"
            onClick={() => setShowCloseConfirm(false)}
            aria-hidden="true"
          />
        </div>
      )}
    </div>
  );
};

export default ConversationDetail;
